/**
 * Per-event setup journey (Setup Hub) — mirror of the backend
 * org.nikita.app.event_setup_plan module. One plan per event, one entry per
 * section; `proposal` is an OPAQUE serialized JSON string owned by the FE
 * (direction-pack shapes, see pages/dashboard/create-event/directions/types.ts).
 */

export type SetupSectionKey =
  | "EMAILS"
  | "WORKFLOWS"
  | "OFFERING"
  | "WEBSITE"
  | "GOALS"
  | "GUESTS"
  | "ADDITIONAL_FIELDS"
  | "FILTERS"
  | "SEATING"
  | "CHECKIN"
  | "DOCUMENTS";

/** Every selectable section — the interview picks a relevant subset. */
export const SETUP_SECTION_POOL: readonly SetupSectionKey[] = [
  "EMAILS",
  "WORKFLOWS",
  "OFFERING",
  "WEBSITE",
  "GOALS",
  "GUESTS",
  "ADDITIONAL_FIELDS",
  "FILTERS",
  "SEATING",
  "CHECKIN",
  "DOCUMENTS",
] as const;

export type SetupSectionStatus =
  | "PROPOSED"
  | "BUILDING"
  | "BUILT"
  | "SKIPPED"
  | "DONE_MANUALLY";

/** One created (or failed) object of a built section (mirror of PackItemResult). */
export interface SetupResultLink {
  kind: string;
  /** Pack item key (e.g. DirectionPackEmail.key) for cross-section reference resolution. */
  key?: string;
  /** Id of the created object (template id, workflow id, ...). */
  refId?: string;
  title?: string;
  /** Relative FE path to open the created object. */
  url?: string;
  ok: boolean;
  error?: string;
}

export interface SetupPlanSection {
  key: SetupSectionKey;
  status: SetupSectionStatus;
  /** The user's taste brief from the create-event interview ("" when none). */
  brief: string;
  /** Serialized JSON proposal (section-specific shape), "" / undefined when none. */
  proposal?: string;
  /**
   * Art. 50 Abs. 2 KI-VO (plan A2): server-set, read-only — the AI tool that
   * wrote or reworked this section's `proposal` (the `/proposal` route or an
   * AI session; sticky, never cleared). The "Build" sends it as the
   * `aiContentTool` of every object it creates from the proposal. Never part
   * of a section update: the server ignores it and keeps its own value.
   */
  proposalAiTool?: string | null;
  resultLinks?: SetupResultLink[];
  statusChangedAt?: string;
}

export interface SetupPlan {
  setupPlanId?: string;
  eventId?: string;
  /** The create-event wizard conversation this plan was distilled from. */
  sourceConversationId?: string;
  /** Chosen direction archetype id, null/undefined for scratch/template creates. */
  directionId?: string | null;
  sections: SetupPlanSection[];
  createdAt?: string;
  updatedAt?: string;
}

/** Partial single-section update: omitted field = leave unchanged. */
export interface SetupSectionUpdate {
  status?: SetupSectionStatus;
  brief?: string;
  proposal?: string;
  resultLinks?: SetupResultLink[];
}

/** A section counts as resolved once it is built or done by hand. */
export function isSectionResolved(status: SetupSectionStatus): boolean {
  return status === "BUILT" || status === "DONE_MANUALLY";
}

/**
 * A section is "open" (still shown in the onboarding-panel setup list + the
 * open count) while it is neither resolved NOR skipped. Skipping is the user's
 * "I'll handle this myself" — it drops the section from the list; auto-detect
 * later flips it to DONE_MANUALLY when the work is actually there.
 */
export function isSectionOpen(status: SetupSectionStatus): boolean {
  return !isSectionResolved(status) && status !== "SKIPPED";
}
